import React from 'react';
import { Input, Button } from 'reactstrap';
import { NO_VALUE, YES_NO_OPTIONS, YES_VALUE } from '../../../../../../slices/marketplace/loi-form/term-form';
import CurrencyInput from 'react-currency-input-field';
import Helpers from '../../../../../../utils/helper';
import { validatePercentageField, validateNonNegativeIntegerField } from '../../../../../../utils/percentFieldValidator';
import InfoTooltip from '../../../../../../components/info-tooltip/info-tooltip';

export interface CellHidingRule {
    column: string;
    dependsOnColumn: string;
    visibleWhen?: string;
    hiddenWhen?: string;
}

export type DetailRow = Record<string, string | null>;

export interface DetailColumnDef {
    key: string;
    header?: string;
    type?: 'currency' | 'percent' | 'select' | 'yesno' | 'days' | 'textarea';
    selectOptions?: string[];
    selectOptionLabels?: Record<string, string>;
    thClassName?: string;
    thStyle?: React.CSSProperties;
    naWhenEmpty?: boolean;
    headerTooltip?: string;
}

const PAYMENT_NUMBER_COLUMN = 'Payment (#)';

export interface TermsFormDetailBlockProps {
    className?: string;
    title: string;
    columns: DetailColumnDef[];
    rows: DetailRow[];
    readOnly: boolean;
    onRowsChange: (rows: DetailRow[]) => void;
    displayTotal?: number | null;
    showTotalRow?: boolean;
    onAddRow?: () => void;
    onRemoveRow?: (rowIndex: number) => void;
    singleRow?: boolean;
    hideAddRow?: boolean;
    showPaymentNumber?: boolean;
    paymentNumberHeader?: string;
    showRowBorder?: boolean;
    hideLastRowBorder?: boolean;
    invalidCells?: Set<string>;
    isBuyerReview?: boolean;
    isBuyerReviewConfirmed?: boolean;
    rowComments?: (string | null)[];
    onRowCommentChange?: (rowIndex: number, comment: string | null) => void;
    cellHidingRules?: CellHidingRule[];
    isAdmin?: boolean;
    adminRowComments?: (string | null)[];
    onAdminRowCommentChange?: (rowIndex: number, adminComment: string | null) => void;
}

export const TermsFormDetailBlock = (props: TermsFormDetailBlockProps) => {
    const {
        className,
        title,
        columns,
        rows,
        readOnly,
        onRowsChange,
        displayTotal,
        showTotalRow,
        onAddRow,
        onRemoveRow,
        singleRow,
        hideAddRow,
        showPaymentNumber,
        paymentNumberHeader = PAYMENT_NUMBER_COLUMN,
        invalidCells,
        isBuyerReview,
        isBuyerReviewConfirmed,
        rowComments,
        onRowCommentChange,
        cellHidingRules = [],
        isAdmin,
        adminRowComments,
        onAdminRowCommentChange,
    } = props;

    const showCommentColumn = isBuyerReview || !!isBuyerReviewConfirmed;
    const isAdminCommentEditable = !!isAdmin && !readOnly;
    const showAdminCommentColumn = showCommentColumn || isAdminCommentEditable;

    const rulesByColumn = new Map<string, CellHidingRule>();
    cellHidingRules.forEach((rule) => rulesByColumn.set(rule.column, rule));
    const isCellHiddenByRule = (row: DetailRow, colKey: string) => {
        const rule = rulesByColumn.get(colKey);
        if (!rule) return false;
        const value = row[rule.dependsOnColumn];
        if (rule.hiddenWhen !== undefined) return !value || value === rule.hiddenWhen;
        return value !== rule.visibleWhen;
    };

    const showRemoveRow = !readOnly && !singleRow && !!onRemoveRow;

    const displayRows: DetailRow[] =
        singleRow && rows.length === 0
            ? [columns.reduce<DetailRow>((acc, c) => ({ ...acc, [c.key]: null }), {})]
            : rows;

    const isColumnHiddenByRule = (colKey: string) =>
        rulesByColumn.has(colKey) && displayRows.every((row) => isCellHiddenByRule(row, colKey));
    const visibleColumns = columns.filter((col) => !isColumnHiddenByRule(col.key));

    const updateCell = (rowIndex: number, colKey: string, value: string | null) => {
        if (singleRow && rows.length === 0) {
            onRowsChange([{ ...displayRows[0], [colKey]: value }]);
            return;
        }
        const next = rows.map((r, i) =>
            i === rowIndex ? { ...r, [colKey]: value } : r
        );
        onRowsChange(next);
    };

    const renderCell = (col: DetailColumnDef, rowIndex: number, row: DetailRow): React.ReactNode => {
        const key = col.key;
        const val = row[key] ?? '';
        if (isCellHiddenByRule(row, key)) {
            return null;
        }
        const isCellInvalid = invalidCells?.has(`${rowIndex}:${key}`) ?? false;
        const isCellDisabled = readOnly;
        const showInvalid = isCellInvalid;
        const displayVal =
            col.type === 'percent' && val !== ''
                ? `${val}%`
                : val;

        if (col.type === 'yesno') {
            return (
                <Input
                    type="select"
                    value={val === YES_VALUE ? YES_VALUE : NO_VALUE}
                    disabled={isCellDisabled}
                    onChange={(e) => updateCell(rowIndex, key, e.target.value)}
                    invalid={showInvalid}
                    className="form-control loi-form-input loi-form-yesno-input"
                    aria-label={key}>
                    {YES_NO_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </Input>
            );
        }

        if (col.type === 'select') {
            return (
                <Input
                    type="select"
                    value={val}
                    disabled={isCellDisabled}
                    onChange={(e) => updateCell(rowIndex, key, e.target.value === '' ? null : e.target.value)}
                    invalid={showInvalid}
                    className="form-control loi-form-input"
                    aria-label={key}>
                    <option value="" />
                    {(col.selectOptions ?? []).map((opt) => (
                        <option key={opt} value={opt}>
                            {col.selectOptionLabels?.[opt] ?? opt}
                        </option>
                    ))}
                </Input>
            );
        }

        if (col.type === 'currency') {
            return (
                <CurrencyInput
                    name={`${title}-${rowIndex}-${key}`}
                    value={val}
                    allowDecimals
                    allowNegativeValue={false}
                    prefix="$"
                    onValueChange={(value) =>
                        updateCell(rowIndex, key, value === undefined || value === '' || value === 'NaN' ? null : value)
                    }
                    disabled={isCellDisabled}
                    className={`form-control loi-form-input${showInvalid ? ' is-invalid' : ''}`}
                    aria-label={key}
                    placeholder={col.naWhenEmpty ? 'N/A' : undefined}
                />
            );
        }

        if (col.type === 'percent') {
            return (
                <Input
                    type="text"
                    value={displayVal}
                    disabled={isCellDisabled}
                    onKeyDown={(event) => { if (validatePercentageField(event, val)) event.preventDefault(); }}
                    onChange={(event) => {
                        let value = event.target.value;
                        if (!value.includes('%') && Number(value) === Number(val)) {
                            value = value.slice(0, -1);
                        }
                        const result = value.replace('%', '');
                        updateCell(rowIndex, key, result === '' ? null : result);
                    }}
                    invalid={showInvalid}
                    className="form-control loi-form-input loi-form-percent-input"
                    aria-label={key}
                />
            );
        }

        if (col.type === 'days') {
            return (
                <Input
                    type="text"
                    value={val}
                    disabled={isCellDisabled}
                    onKeyDown={(event) => { if (validateNonNegativeIntegerField(event)) event.preventDefault(); }}
                    onChange={(event) => {
                        const result = event.target.value.replace(/[^0-9]/g, '');
                        updateCell(rowIndex, key, result === '' ? null : result);
                    }}
                    invalid={showInvalid}
                    className="form-control loi-form-input loi-form-months-input"
                    aria-label={key}
                />
            );
        }

        if (col.type === 'textarea') {
            return (
                <Input
                    type="textarea"
                    value={val}
                    disabled={isCellDisabled}
                    onChange={(e) => updateCell(rowIndex, key, e.target.value === '' ? null : e.target.value)}
                    invalid={showInvalid}
                    className="terms-form-comment-input"
                    style={{ width: '100%' }}
                    aria-label={key}
                />
            );
        }

        return (
            <Input
                type="text"
                value={readOnly && col.naWhenEmpty && val === '' ? 'N/A' : val}
                disabled={isCellDisabled}
                onChange={(e) => updateCell(rowIndex, key, e.target.value === '' ? null : e.target.value)}
                invalid={showInvalid}
                className="form-control loi-form-input"
                aria-label={key}
            />
        );
    };

    return (
        <div
            className={`terms-form-detail-block${className ? ` ${className}` : ''}${rows.length > 0 ? ' terms-form-detail-block--has-rows' : ''}`}>
            <div className="terms-form-block-name">{title}</div>
            <div className="terms-form-table-scroll">
                <div className="terms-form-price-summary-table-container">
                    <table className="terms-form-price-summary-table">
                        <thead>
                            <tr>
                                {showPaymentNumber && (
                                    <th style={{ width: '90px', minWidth: '90px' }}>{paymentNumberHeader}</th>
                                )}
                                {visibleColumns.map((col) => {
                                    const thClassName = col.thClassName ?? (() => {
                                        switch (col.type) {
                                            case 'currency': return 'currency-column';
                                            case 'yesno': return 'yesno-column';
                                            default: return undefined;
                                        }
                                    })();
                                    const thStyle = col.thStyle ?? (() => {
                                        switch (col.type) {
                                            case 'yesno': return { width: '80px', minWidth: '80px' };
                                            default: return { width: '135px', minWidth: '135px' };
                                        }
                                    })();
                                    const label = col.header ?? col.key;
                                    return (
                                        <th key={col.key} className={thClassName} style={thStyle}>
                                            {label}
                                            {col.headerTooltip && (
                                                <InfoTooltip
                                                    text={col.headerTooltip}
                                                    idText={`terms-form-col-tooltip-${`${title}-${col.key}`.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                                                />
                                            )}
                                        </th>
                                    );
                                })}
                                {showAdminCommentColumn && (
                                    <th className="terms-form-col-comment terms-form-col-admin-comment">Admin Comment</th>
                                )}
                                {showCommentColumn && (
                                    <th className="terms-form-col-comment">Buyer Comment</th>
                                )}
                                {showRemoveRow && <th style={{ width: '32px' }} />}
                            </tr>
                        </thead>
                        <tbody>
                            {displayRows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {showPaymentNumber && (
                                        <td className="table-label-cell">Payment {rowIndex + 1}</td>
                                    )}
                                    {visibleColumns.map((col) => (
                                        <td key={col.key}>{renderCell(col, rowIndex, row)}</td>
                                    ))}
                                    {showAdminCommentColumn && (
                                        <td className="terms-form-col-comment terms-form-col-admin-comment">
                                            <Input
                                                type="textarea"
                                                value={adminRowComments?.[rowIndex] ?? ''}
                                                readOnly={!isAdminCommentEditable}
                                                onChange={(e) =>
                                                    onAdminRowCommentChange?.(rowIndex, e.target.value === '' ? null : e.target.value)
                                                }
                                                className="terms-form-comment-input"
                                                aria-label={`Admin comment for row ${rowIndex}`}
                                            />
                                        </td>
                                    )}
                                    {showCommentColumn && (
                                        <td className="terms-form-col-comment">
                                            <Input
                                                type="textarea"
                                                value={rowComments?.[rowIndex] ?? ''}
                                                readOnly={!isBuyerReview}
                                                onChange={(e) =>
                                                    onRowCommentChange?.(rowIndex, e.target.value === '' ? null : e.target.value)
                                                }
                                                className="terms-form-comment-input"
                                                aria-label={`Comment for row ${rowIndex}`}
                                            />
                                        </td>
                                    )}
                                    {showRemoveRow && (
                                        <td>
                                            <Button
                                                color="link"
                                                className="terms-form-remove-row-btn"
                                                onClick={() => onRemoveRow!(rowIndex)}
                                                aria-label="Remove row">
                                                ×
                                            </Button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            {showTotalRow && displayTotal != null && (
                <div className="terms-form-detail-block terms-form-summary-layout">
                    <div className="terms-form-form-content">
                        <div className="terms-form-summary-row details-block-summary-total">
                            <div className="block-total-label">Total</div>
                            <div className="block-total-value">
                                <Input
                                    type="text"
                                    value={Helpers.currencyFormat(displayTotal, false)}
                                    disabled
                                    readOnly
                                    className="form-control loi-form-input"
                                    aria-label="Total"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!readOnly && onAddRow && !singleRow && !hideAddRow && (
                <Button
                    color="link"
                    className="terms-form-add-payment-btn"
                    onClick={onAddRow}>
                    + Add another payment
                </Button>
            )}
        </div>
    );
};
